// src/pages/Home/index.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Input,
  Space,
  Tag,
  Typography,
  Empty,
  Button,
  Pagination,
  Select,
  DatePicker,
  Segmented,
} from "antd";
import { useNavigate } from "react-router-dom";
import PostCard from "../../components/PostCard/PostCard";
import { listPosts, listBookmarks } from "../../api/mockData";
import dayjs from "dayjs";

export default function Home({ groupByDay = false }) {
  const navigate = useNavigate();
  const [kw, setKw] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [sortKey, setSortKey] = useState("newest");
  const [tagMatch, setTagMatch] = useState("any");
  const [showAllSidebar, setShowAllSidebar] = useState(false);
  const [posts, setPosts] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  useEffect(() => {
    listPosts().then(setPosts);
    setBookmarks(listBookmarks());
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set);
  }, [posts]);

  const allCategories = useMemo(() => {
    const set = new Set();
    posts.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set);
  }, [posts]);

  const filtered = useMemo(() => {
    const list = posts.filter((p) => {
      const tagText = (p.tags || []).join(" ");
      const hitKw =
        !kw ||
        p.title?.toLowerCase().includes(kw.toLowerCase()) ||
        p.summary?.toLowerCase().includes(kw.toLowerCase()) ||
        p.content?.toLowerCase().includes(kw.toLowerCase()) ||
        tagText.toLowerCase().includes(kw.toLowerCase()) ||
        p.category?.toLowerCase().includes(kw.toLowerCase());

      const hitTag = !activeTag || (p.tags || []).includes(activeTag);
      const hitCategory = !selectedCategory || p.category === selectedCategory;
      const rangeHit =
        !dateRange ||
        (!dateRange[0] && !dateRange[1]) ||
        (p.createdAt >= dateRange[0]?.valueOf() &&
          p.createdAt <= dateRange[1]?.valueOf());
      const selectedHit =
        !selectedTags.length ||
        (tagMatch === "all"
          ? selectedTags.every((t) => (p.tags || []).includes(t))
          : selectedTags.some((t) => (p.tags || []).includes(t)));

      return hitKw && hitTag && hitCategory && rangeHit && selectedHit;
    });

    const sorted = [...list];
    if (sortKey === "oldest") {
      sorted.sort((a, b) => a.createdAt - b.createdAt);
    } else if (sortKey === "hot") {
      sorted.sort((a, b) => (b.content?.length || 0) - (a.content?.length || 0));
    } else {
      sorted.sort((a, b) => b.createdAt - a.createdAt);
    }
    return sorted;
  }, [
    posts,
    kw,
    activeTag,
    selectedTags,
    selectedCategory,
    dateRange,
    sortKey,
    tagMatch,
  ]);

  useEffect(() => {
    setPage(1);
  }, [kw, activeTag, selectedTags, selectedCategory, dateRange, sortKey, tagMatch]);

  const hotPosts = useMemo(() => {
    return [...posts]
      .sort((a, b) => (b.content?.length || 0) - (a.content?.length || 0))
      .slice(0, 3);
  }, [posts]);

  const recentPosts = useMemo(() => posts.slice(0, 3), [posts]);
  const totalWords = useMemo(
    () => posts.reduce((sum, p) => sum + (p.content?.length || 0), 0),
    [posts]
  );

  const groupedPosts = useMemo(() => {
    if (!groupByDay) return [];
    const buckets = new Map();
    filtered.forEach((p) => {
      const day = dayjs(p.createdAt).format("YYYY-MM-DD");
      if (!buckets.has(day)) buckets.set(day, []);
      buckets.get(day).push(p);
    });
    return Array.from(buckets.entries()).map(([day, list]) => ({ day, list }));
  }, [filtered, groupByDay]);

  const pagedPosts = useMemo(() => {
    if (groupByDay) return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize, groupByDay]);

  const sidebarItems = [
    {
      key: "notice",
      title: "公告/签名",
      content: (
        <Typography.Text style={{ color: "var(--text-secondary)" }}>
          保持更新，记录灵感；欢迎留言交流。
        </Typography.Text>
      ),
    },
    {
      key: "tags",
      title: "标签云",
      content: (
        <Space wrap>
          {allTags.length ? (
            allTags.map((t) => (
              <Tag key={t} className="themed-tag">
                {t}
              </Tag>
            ))
          ) : (
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              暂无标签
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: "bookmarks",
      title: "书签",
      content: (
        <Space direction="vertical" size={6}>
          {bookmarks.length ? (
            bookmarks.slice(0, 5).map((b) => (
              <Button
                key={b.id}
                type="text"
                onClick={() => navigate(`/post/${b.postId}?pos=${b.scrollTop}`)}
                style={{ padding: 0, color: "var(--text-primary)", textAlign: "left" }}
              >
                {b.title}
              </Button>
            ))
          ) : (
            <Typography.Text style={{ color: "var(--text-secondary)" }}>
              暂无书签
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      key: "hot",
      title: "热门文章",
      content: (
        <Space direction="vertical" size={6}>
          {hotPosts.map((p) => (
            <Button
              key={p.id}
              type="text"
              onClick={() => navigate(`/post/${p.id}`)}
              style={{ padding: 0, color: "var(--text-primary)", textAlign: "left" }}
            >
              {p.title}
            </Button>
          ))}
        </Space>
      ),
    },
    {
      key: "recent",
      title: "最近更新",
      content: (
        <Space direction="vertical" size={6}>
          {recentPosts.map((p) => (
            <Button
              key={p.id}
              type="text"
              onClick={() => navigate(`/post/${p.id}`)}
              style={{ padding: 0, color: "var(--text-primary)", textAlign: "left" }}
            >
              {p.title}
            </Button>
          ))}
        </Space>
      ),
    },
    {
      key: "stats",
      title: "站点统计",
      content: (
        <Space size={12}>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            文章 {posts.length}
          </Typography.Text>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            标签 {allTags.length}
          </Typography.Text>
          <Typography.Text style={{ color: "var(--text-secondary)" }}>
            字数 {totalWords}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: "about",
      title: "关于我",
      content: (
        <Typography.Text style={{ color: "var(--text-secondary)" }}>
          热爱技术与设计，喜欢把复杂问题拆成清晰的故事。
        </Typography.Text>
      ),
    },
    {
      key: "links",
      title: "友链入口",
      content: (
        <Space direction="vertical" size={4}>
          <Button type="link" style={{ padding: 0 }}>
            申请友链
          </Button>
          <Button type="link" style={{ padding: 0 }}>
            查看友链
          </Button>
        </Space>
      ),
    },
  ];

  const visibleSidebarItems = showAllSidebar
    ? sidebarItems
    : sidebarItems.slice(0, 3);

  return (
    <div className="home-page">
      <div className="home-layout">
        <div className="home-main">
          <div className="home-rail">
            <div className="home-rail-track">
              <div className="home-rail-dot" />
            </div>
            <div className="home-rail-content">
              <Card
                className="themed-card home-search-card"
                bodyStyle={{ padding: "20px 22px" }}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" size={14} style={{ width: "100%" }}>
                  <Space direction="vertical" style={{ width: "100%" }} size={10}>
                    <Input.Search
                      allowClear
                      placeholder="搜索标题/摘要/正文/标签/分类..."
                      value={kw}
                      onChange={(e) => setKw(e.target.value)}
                    />

                    <Space wrap>
                      <Tag
                        className="themed-tag"
                        style={{
                          cursor: "pointer",
                          background: !activeTag
                            ? "var(--accent-gradient)"
                            : "var(--tag-bg)",
                          color: !activeTag ? "#fff" : "var(--tag-color)",
                          borderColor: !activeTag ? "transparent" : "var(--tag-border)",
                        }}
                        onClick={() => setActiveTag("")}
                      >
                        全部
                      </Tag>
                      {allTags.map((t) => (
                        <Tag
                          key={t}
                          className="themed-tag"
                          style={{
                            cursor: "pointer",
                            background:
                              activeTag === t
                                ? "var(--accent-gradient)"
                                : "var(--tag-bg)",
                            color: activeTag === t ? "#fff" : "var(--tag-color)",
                            borderColor:
                              activeTag === t ? "transparent" : "var(--tag-border)",
                          }}
                          onClick={() => setActiveTag(t)}
                        >
                          {t}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                  <div className="home-advanced-filters">
                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                      <Space wrap>
                        <Typography.Text style={{ color: "var(--text-secondary)" }}>
                          标签筛选
                        </Typography.Text>
                        <Segmented
                          size="small"
                          value={tagMatch}
                          onChange={setTagMatch}
                          options={[
                            { label: "任意匹配", value: "any" },
                            { label: "全部匹配", value: "all" },
                          ]}
                        />
                      </Space>
                      <Select
                        mode="multiple"
                        allowClear
                        placeholder="选择标签（可多选）"
                        value={selectedTags}
                        onChange={setSelectedTags}
                        options={allTags.map((t) => ({ label: t, value: t }))}
                        style={{ width: "100%" }}
                      />
                      <Space wrap style={{ width: "100%" }}>
                        <Select
                          allowClear
                          placeholder="选择分类"
                          value={selectedCategory || undefined}
                          onChange={(value) => setSelectedCategory(value || "")}
                          options={allCategories.map((c) => ({
                            label: c,
                            value: c,
                          }))}
                          style={{ minWidth: 220 }}
                        />
                        <DatePicker.RangePicker
                          allowClear
                          value={dateRange || undefined}
                          onChange={setDateRange}
                          style={{ minWidth: 240 }}
                        />
                        <Select
                          value={sortKey}
                          onChange={setSortKey}
                          options={[
                            { label: "最新发布", value: "newest" },
                            { label: "最早发布", value: "oldest" },
                            { label: "正文长度", value: "hot" },
                          ]}
                          style={{ minWidth: 160 }}
                        />
                        <Button
                          onClick={() => {
                            setKw("");
                            setActiveTag("");
                            setSelectedTags([]);
                            setSelectedCategory("");
                            setDateRange(null);
                            setSortKey("newest");
                            setTagMatch("any");
                          }}
                        >
                          清空筛选
                        </Button>
                      </Space>
                    </Space>
                  </div>
                </Space>
              </Card>

              {filtered.length ? (
                groupByDay ? (
                  <div className="home-day-list">
                    {groupedPosts.map((group) => (
                      <div key={group.day} className="home-day-group">
                        <div className="home-day-title">{group.day}</div>
                        <div className="home-posts">
                          {group.list.map((p) => (
                            <PostCard key={p.id} post={p} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="home-posts home-rail-posts">
                    {pagedPosts.map((p, idx) => (
                      <PostCard
                        key={p.id}
                        post={p}
                        keyword={kw}
                        className="list-item-animate"
                        style={{ animationDelay: `${idx * 60}ms` }}
                      />
                    ))}
                    {filtered.length > pageSize ? (
                      <div style={{ marginTop: 16 }}>
                        <Pagination
                          current={page}
                          pageSize={pageSize}
                          total={filtered.length}
                          showSizeChanger
                          onChange={(nextPage, nextSize) => {
                            setPage(nextPage);
                            setPageSize(nextSize);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )
              ) : (
                <Card className="themed-card" style={{ width: "100%" }}>
                  <Empty description="没有匹配的文章" />
                </Card>
              )}
            </div>
          </div>
        </div>

        <aside className="home-sidebar">
          {visibleSidebarItems.map((item) => (
            <Card
              key={item.key}
              className="themed-card"
              bodyStyle={{ padding: "16px 18px" }}
            >
              <Typography.Text
                className="sidebar-title"
                style={{ color: "var(--text-secondary)" }}
              >
                {item.title}
              </Typography.Text>
              <div style={{ marginTop: 10 }}>{item.content}</div>
            </Card>
          ))}

          <Button
            type="text"
            className="themed-primary"
            onClick={() => setShowAllSidebar((v) => !v)}
            style={{ width: "100%" }}
          >
            {showAllSidebar ? "收起信息栏" : "展开全部信息"}
          </Button>
        </aside>
      </div>
    </div>
  );
}
